import React, { useEffect } from "react";
import { Box, Button, Grid } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import AppForm from "components/Shared/AppForm";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useNavigate } from "react-router-dom";
import { formHookInputHelper } from "utils/formHookField";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetBranchByRegionDDLQuery } from "store/services/branchApi";
import { useLazyGetDDLRestrictedRegionListQuery } from "store/services/LocationsApi/regionApi";

const VendorModalForm = ({
  vendorDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const customerTypeEnums = ["Individual", "Business"];
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      address: "",
      type: "Individual",
      controllingUser: vendorDetail?.controllingUser,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
      province: [],
      branch: [],
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const selectedProvinces = useWatch({ control, name: "province" });
  const [branchOptions, setBranchOptions] = React.useState([]);

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  useEffect(() => {
    if (vendorDetail) {
      reset({
        name: vendorDetail.name ?? "",
        phone: vendorDetail.phone ?? "",
        email: vendorDetail.email ?? "",
        address: vendorDetail.address ?? "",
        type: vendorDetail.type ?? "Individual",
        controllingUser:
          {
            name: vendorDetail?.controllingUser?.name,
            id: vendorDetail?.controllingUser?._id,
          } ?? "",
      });
    }
  }, [vendorDetail, reset]);

  const [
    fetchUsers,
    { data: userList = [], isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchProvinces, { data: provincesData, isLoading: loadingProvinces }] =
    useLazyGetDDLRestrictedRegionListQuery();
  const [fetchBranches, { isLoading: loadingBranches, error: branchError }] =
    useLazyGetBranchByRegionDDLQuery();

  const loading = isBusy || loadingUsers || fetchingUsers;

  const handlePayload = (data) => {
    const _payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      controllingUser: data?.controllingUser?._id,
      province: data?.province?.map((province) => province.id || province._id) || [],
      branch: data?.branch?.map((branch) => branch.id || branch._id) || [],
    };
    onSubmit(_payload);
  };

  React.useEffect(() => {
    if (!_watchAccount?._id) return;
    fetchUsers({ account: _watchAccount?._id });
  }, [fetchUsers, _watchAccount?._id]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id || viewEntity?.entity?.account,
      });
    }
  }, [
    isAccountAdmin,
    isSystemAdmin,
    _watchAccount?._id,
    fetchEntities,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    const accountId = _watchAccount?._id || viewEntity?.entity?.account;
    const entityId = _watchEntity?._id || viewEntity?.entity?._id;
    fetchProvinces({ account: accountId, entity: entityId });
  }, [fetchProvinces, _watchAccount?._id, _watchEntity?._id, viewEntity]);

  React.useEffect(() => {
    const accountId = _watchAccount?._id || viewEntity?.entity?.account;
    const entityId = _watchEntity?._id || viewEntity?.entity?._id;
    if (!accountId || !entityId || !(selectedProvinces || []).length) {
      setBranchOptions([]);
      setValue("branch", []);
      return;
    }

    Promise.allSettled(
      (selectedProvinces || []).map((province) =>
        fetchBranches({
          regionId: province.id || province._id,
          account: accountId,
          entity: entityId,
        }).unwrap()
      )
    ).then((results) => {
      const options = results
        .filter((result) => result.status === "fulfilled")
        .flatMap((result) => result.value?.data || [])
        .filter((branch, index, all) => all.findIndex((item) => item._id === branch._id) === index)
        .map((branch) => ({
          label: `${branch.name || "Unknown"} - ${branch.code || "N/A"}`,
          id: branch._id,
          provinceId: branch.region?._id || branch.region,
        }));
      setBranchOptions(options);
      setValue("branch", getValues("branch").filter((selectedBranch) =>
        options.some((option) => option.id === selectedBranch.id)
      ));
    });
  }, [selectedProvinces, _watchAccount, _watchEntity, viewEntity, fetchBranches, setValue, getValues]);

  return (
    <AppForm loading={loading}>
      <AppForm.Content>
        <Grid container spacing={2}>
          {(isAccountAdmin || isSystemAdmin) && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Account"
                      required
                      value={value ?? ""}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("entity", "");
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled={isAccountAdmin}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Entity"
                      value={value ?? ""}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("insurance_id", "");
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide full Name." }}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  required
                  fullWidth
                  label="Full Name"
                  placeholder="Enter Full Name"
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Please provide an email.",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  fullWidth
                  required
                  label="Email"
                  placeholder="Enter email"
                  error={!!errors?.email}
                  helperText={errors?.email?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",

                validate: (value) => {
                  const digits = value.replace(/\D/g, "");
                  if (digits.length < 7 || digits.length > 17) {
                    return "Phone number must be between 9 and 16 digits";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <CustomCountryPhoneInput
                  placeholder="Phone Number"
                  label="Phone*"
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="address"
              control={control}
              rules={{ required: "Please provide address." }}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  required
                  fullWidth
                  label="Address"
                  placeholder="Enter Address"
                  error={!!errors?.address}
                  helperText={errors?.address?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="province"
              control={control}
              rules={{ required: "Province is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  multiple
                  fullWidth
                  required
                  label="Province"
                  options={provincesData?.data || provincesData || []}
                  loading={loadingProvinces}
                  value={field.value || []}
                  onChange={(_, data) => {
                    field.onChange(data);
                    const provinceIds = data.map((province) => province.id || province._id);
                    setValue("branch", getValues("branch").filter((branch) =>
                      !branch.provinceId || provinceIds.includes(branch.provinceId)
                    ));
                  }}
                  getOptionLabel={(option) => option?.label || option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    (option?.id || option?._id) === (value?.id || value?._id)
                  }
                  helperText={errors?.province?.message}
                  error={!!errors?.province}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="branch"
              control={control}
              rules={{ required: "Branch is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  multiple
                  fullWidth
                  required
                  label="Branch"
                  options={branchOptions}
                  loading={loadingBranches}
                  disabled={!(selectedProvinces || []).length}
                  value={field.value || []}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  helperText={errors?.branch?.message || (branchError?.status !== 404 && branchError ? "Failed to load branches" : "")}
                  error={!!errors?.branch || (branchError?.status !== 404 && !!branchError)}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="controllingUser"
              control={control}
              rules={{ required: "Please select a controlling user." }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Controlling User"
                  loading={loadingUsers || fetchingUsers}
                  loadingText="Loading Users..."
                  options={userList || []}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  error={!!errors?.controllingUser}
                  helperText={errors?.controllingUser?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Please select a type." }}
              render={(props) => (
                <BaseSelect
                  {...formHookInputHelper(props)}
                  fullWidth
                  required
                  label="Type"
                  options={customerTypeEnums}
                />
              )}
            />
          </Grid>
        </Grid>

        <Box
          paddingY={2}
          gap={2}
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button disabled={isBusy} onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={handleSubmit(handlePayload)}
          >
            {vendorDetail ? "Update" : "Add"}
          </Button>
        </Box>
      </AppForm.Content>
    </AppForm>
  );
};

export default VendorModalForm;
